/**
 * Seasonal events: a few weeks a year when the app dresses up.
 *
 * Every date here is a calendar day in the UK, where the app's players are, and
 * every window is inclusive at both ends. The calendar repeats yearly, so there
 * is nothing to update each year — Christmas simply wraps over New Year.
 *
 * What a player wears for an event is kept apart from what they normally wear
 * (see `SeasonalWear`), so when the event ends everyone is back in their own
 * clothes without anything having to be undone.
 */

export type SeasonId = 'halloween' | 'christmas' | 'valentines' | 'spring' | 'summer';

export interface Season {
  id: SeasonId;
  name: string;
  icon: string;
  /** First day, as [month 1-12, day]. */
  from: [number, number];
  /** Last day, inclusive. Earlier in the year than `from` when it wraps. */
  to: [number, number];
  /** Accent for the banner and the event's own skin swatch. */
  glow: string;
}

export const SEASONS: Season[] = [
  { id: 'valentines', name: "Valentine's Day", icon: '💘', from: [2, 14], to: [2, 14], glow: '#ff4f8b' },
  { id: 'spring', name: 'Spring Fling', icon: '🌷', from: [4, 1], to: [4, 30], glow: '#9dff7a' },
  { id: 'summer', name: 'Summer Holidays', icon: '🏖️', from: [7, 1], to: [8, 31], glow: '#ffc247' },
  { id: 'halloween', name: 'Halloween', icon: '🎃', from: [10, 15], to: [11, 1], glow: '#ff7a1a' },
  { id: 'christmas', name: 'Christmas', icon: '🎄', from: [12, 17], to: [1, 2], glow: '#3dff8b' },
];

export const SEASON_IDS: SeasonId[] = SEASONS.map((s) => s.id);

export function isSeasonId(value: unknown): value is SeasonId {
  return typeof value === 'string' && (SEASON_IDS as string[]).includes(value);
}

export function seasonById(id: SeasonId): Season {
  return SEASONS.find((s) => s.id === id)!;
}

/** The UK calendar date of an instant, as numbers. */
export function ukDate(now: number = Date.now()): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date(now));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/** `YYYY-MM-DD` for the UK day an instant falls on. The daily challenge is keyed by it. */
export function ukDayKey(now: number = Date.now()): string {
  const { year, month, day } = ukDate(now);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const ordinal = (month: number, day: number) => month * 100 + day;

function inWindow(season: Season, month: number, day: number): boolean {
  const today = ordinal(month, day);
  const from = ordinal(...season.from);
  const to = ordinal(...season.to);
  return from <= to ? today >= from && today <= to : today >= from || today <= to;
}

let override: SeasonId | null = null;

/**
 * Pretends an event is on, for trying the seasonal things out on a dev server.
 * The worker sets it from a `FORCE_SEASON` variable that production never has.
 */
export function setSeasonOverride(id: SeasonId | null): void {
  override = id;
}

/** The event on now, if any. Windows never overlap, so there is at most one. */
export function currentSeason(now: number = Date.now()): Season | null {
  if (override) return seasonById(override);
  const { month, day } = ukDate(now);
  return SEASONS.find((s) => inWindow(s, month, day)) ?? null;
}

/**
 * When the event that is on now began and ends, as instants, so games played
 * during it can be counted. Generous by a day at each end: the boundaries of a
 * UK calendar day move with the clocks, and a game a few hours either side of
 * the window is not worth arguing about.
 */
export function seasonWindow(season: Season, now: number = Date.now()): { start: number; end: number } {
  const { year, month } = ukDate(now);
  const wraps = ordinal(...season.from) > ordinal(...season.to);
  // A wrapping event seen in January began the previous December.
  const startYear = wraps && month < season.from[0] ? year - 1 : year;
  const endYear = wraps ? startYear + 1 : startYear;
  const day = 86_400_000;
  return {
    start: Date.UTC(startYear, season.from[0] - 1, season.from[1]) - day,
    end: Date.UTC(endYear, season.to[0] - 1, season.to[1]) + 2 * day,
  };
}

/**
 * What a player has put on for an event, over the top of their usual look.
 *
 * Stored on its own, so their normal choices are never overwritten. While the
 * named event is on, each slot set here wins; the moment it ends the whole thing
 * is ignored and they are back to exactly what they wore before.
 */
export interface SeasonalWear {
  season: SeasonId;
  frame?: string;
  entrance?: string;
  font?: string;
  catchphrase?: string;
  /** Worn everywhere, in every game, for the event. */
  skin?: string;
  banner?: string;
}

export const SEASONAL_SLOTS = ['frame', 'entrance', 'font', 'catchphrase', 'banner', 'skin'] as const;
export type SeasonalSlot = (typeof SEASONAL_SLOTS)[number];

/** The overlay that applies right now, or null once its event is over. */
export function activeWear(wear: SeasonalWear | null | undefined, now: number = Date.now()): SeasonalWear | null {
  if (!wear) return null;
  return currentSeason(now)?.id === wear.season ? wear : null;
}

/** How often a seasonal prompt is drawn while its event is on. */
export const SEASONAL_PROMPT_CHANCE = 0.25;

/** Whether a catalogue entry tagged with a season may be drawn now. */
export function seasonAllows(season: SeasonId | undefined, now: number = Date.now()): boolean {
  if (!season) return true;
  return currentSeason(now)?.id === season;
}
